import { BehaviorSubject } from "rxjs";
import { Injectable } from "@angular/core";
import { TreeService } from "./tree.service";
import { SearchFieldsDto } from "../models/search-fields-dto";
import { SearchFieldDto, ThesaurusSearchType } from "../models/search-field-dto";
import { FieldsListsService } from "./fields-lists.service";
import { FieldsListsDto } from "../models/fields-lists-dto";
import { RequestKeys } from "../enums/request-keys.enum";
import { ParamMap } from "@angular/router";
import { MultiLangStrDto } from "../models/multi-lang-str-dto";

@Injectable({
  providedIn: 'root'
})
export class SearchFieldsService {
  private searchFieldsSubject = new BehaviorSubject<SearchFieldsDto>({} as SearchFieldsDto);
  searchFields$ = this.searchFieldsSubject.asObservable();
  fieldsLists: FieldsListsDto = {} as FieldsListsDto;

  constructor(private treeService: TreeService, private fieldsListsService: FieldsListsService) {
    this.fieldsListsService.fieldsLists$.subscribe((fieldsLists) => {
      this.fieldsLists = fieldsLists;
    });
  }
  getSearchFields(key: keyof SearchFieldsDto): SearchFieldDto[] {
    return this.searchFieldsSubject.value[key] || [];
}
  addSearchField(key: keyof SearchFieldsDto, field: SearchFieldDto): void {    
    const currentSearchFields = this.searchFieldsSubject.value;
    if (!currentSearchFields[key]) {
      currentSearchFields[key] = [];
    }
    currentSearchFields[key].push(field);
    this.searchFieldsSubject.next(currentSearchFields);
  }
  updateRegionSearchField(key: keyof SearchFieldsDto, field: SearchFieldDto , regionpolygon:string): void {
    const currentSearchFields = this.searchFieldsSubject.value;
            currentSearchFields[key] = [];
            currentSearchFields[key].push(field);
            this.searchFieldsSubject.next(currentSearchFields);
}

  removeSearchField(key: keyof SearchFieldsDto, field: SearchFieldDto): void {
    const currentSearchFields = this.searchFieldsSubject.value;
    if (currentSearchFields[key]) {
      // Match by ID and label to handle nodes with duplicate IDs but different labels
      const index = currentSearchFields[key].findIndex((f) => {
        if (f.id !== field.id) {
          return false;
        }
        // If labels exist, compare them for exact match
        if (f.label && field.label && typeof f.label !== 'string' && typeof field.label !== 'string') {
          // Compare MultiLangStrDto labels (may have category prefix)
          const fLabelHe = f.label.hebrew?.split(': ').pop() || f.label.hebrew;
          const fLabelEn = f.label.english?.split(': ').pop() || f.label.english;
          const fieldLabelHe = field.label.hebrew?.split(': ').pop() || field.label.hebrew;
          const fieldLabelEn = field.label.english?.split(': ').pop() || field.label.english;
          
          return fLabelHe === fieldLabelHe && fLabelEn === fieldLabelEn;
        }
        // If no label comparison needed, match by ID only
        return true;
      });
      if (index > -1) {
        currentSearchFields[key].splice(index, 1);
        this.searchFieldsSubject.next(currentSearchFields);
      }
    }
  }
  removeAllKeySearchFields(key: keyof SearchFieldsDto): void {
    const currentSearchFields = this.searchFieldsSubject.value;
    if (currentSearchFields[key]) {
        currentSearchFields[key]=[]
        this.searchFieldsSubject.next(currentSearchFields);
    }
  }
  
  addSearchFieldsForIds(ids: string[] | number[] | null, items: any[], fieldKey: keyof SearchFieldsDto, idKey: string, labelKey: string): void {
    ids?.forEach((id: string | number) => {
      const idStr = id.toString();
      const item = items?.find(item => item[idKey]?.toString() === idStr);
      if (item) {
        this.addSearchField(fieldKey, {id: item[idKey].toString(), label: item[labelKey]});
      } else {
        // If item not found in fieldsLists, add it with ID as label
        this.addSearchField(fieldKey, {id: idStr, label: new MultiLangStrDto(idStr, idStr)});
      }
    });
  }

  addSearchFieldsTreeNodes(ids: string[] | null, tree: any, fieldKey: keyof SearchFieldsDto): void {
    ids?.forEach((id: string) => {
      const node = this.treeService.findNodeById(tree, id);
      if (node) {
        this.addSearchField(fieldKey, {
          id: node.data,
          label: new MultiLangStrDto(node.multiLangLabel?.hebrew || '', node.multiLangLabel?.english || ''),
          wid: node.wid?.toString(),
          address: node.address
        });
      }
    });
  }

  addSearchFieldsAdvancedThesaurusByCategory(advancedThesaurus: { [categoryId: string]: Array<{mid: string, searchType: string}> } | null): void {
    if (!advancedThesaurus || !this.fieldsLists.thesaurusAll || !this.fieldsLists.thesaurusAll.categories) {
      return;
    }

    // Process each category
    Object.entries(advancedThesaurus).forEach(([categoryIdStr, items]) => {
      const categoryId = Number(categoryIdStr);
      const category = this.fieldsLists.thesaurusAll.categories.find(cat => cat.categoryId === categoryId);
      
      if (!category) {
        return;
      }

      const categoryName = category.categoryName;

      // For each item (address + searchType) in this category, find the node
      items.forEach((item: {mid: string, searchType: string}) => {
        // Find node by address
        const node = this.treeService.findNodeByAddress(category.tree, item.mid);
        if (node) {
          const prefixedLabel = new MultiLangStrDto(
            `${categoryName.hebrew}: ${node.multiLangLabel?.hebrew || node.label || ''}`,
            `${categoryName.english}: ${node.multiLangLabel?.english || node.label || ''}`
          );
          
          this.addSearchField(RequestKeys.ADVANCED_THESAURUS, {
            id: node.address || node.data.toString(),
            label: prefixedLabel,
            mid: node.data,
            wid: node.wid?.toString(),
            address: node.address,
            categoryId: category.categoryId,
            searchType: item.searchType as ThesaurusSearchType
          });
        }
      });
    });
  }

  addSearchFieldsAdvancedThesaurus(ids: string[] | null): void {
    if (!ids || !this.fieldsLists.thesaurusAll || !this.fieldsLists.thesaurusAll.categories) {
      return;
    }

    // ids are actually addresses for advanced thesaurus
    ids.forEach((address: string) => {
      // Search across all category trees to find the node by address
      for (const category of this.fieldsLists.thesaurusAll.categories) {
        const node = this.treeService.findNodeByAddress(category.tree, address);
        if (node) {
          // Found the node in this category
          const categoryName = category.categoryName;
          const prefixedLabel = new MultiLangStrDto(
            `${categoryName.hebrew}: ${node.multiLangLabel?.hebrew || node.label || ''}`,
            `${categoryName.english}: ${node.multiLangLabel?.english || node.label || ''}`
          );
          
          this.addSearchField(RequestKeys.ADVANCED_THESAURUS, {
            id: node.address || node.data.toString(),
            label: prefixedLabel,
            mid: node.data,
            wid: node.wid?.toString(),
            address: node.address,
            categoryId: category.categoryId,
            searchType: 'branch' // Default search type
          });
          break; // Found it, move to next address
        }
      }
    });
  }
  
  populateSearchFields(request: { [key: string]: any }): void {
    const {
      regionIds,settlementIds,persons, thesaurusWids, advancedThesaurus, freeText, licenses, expeditions, expeditionLicenses, excavations, excavationLicenses, surveys, surveyLicenses, entityTypes,regions,
      periodMids, findTypeMids, materialMids , hachrazot, heritageSiteIds
    } = request;
    this.addSearchFieldsForIds(persons, this.fieldsLists.persons, RequestKeys.PERSONS, 'entityPersonId', 'name');
    this.addSearchFieldsForIds(settlementIds, this.fieldsLists.settlements, RequestKeys.SETTLEMENT_IDS, 'id', 'name');
    this.addSearchFieldsForIds(hachrazot, this.fieldsLists.hachrazot, RequestKeys.HACHRAZOT, 'hachrazaEntityId', 'name');
    this.addSearchFieldsTreeNodes(heritageSiteIds, this.fieldsLists.heritageSites, RequestKeys.HERITAGE_SITE_IDS);
    this.addSearchFieldsForIds(regionIds, this.fieldsLists.regions, RequestKeys.REGION_IDS, 'id', 'name');
    this.addSearchFieldsForIds(thesaurusWids, this.fieldsLists.thesaurus, RequestKeys.THESAURUS_WIDS, 'wid', 'wordName');
    this.addSearchFieldsForIds(licenses, this.fieldsLists.licenses, RequestKeys.LICENSES, 'licenseId', 'longName');
    this.addSearchFieldsForIds(expeditionLicenses, this.fieldsLists.licenses, RequestKeys.EXPEDITION_LICENSES, 'licenseId', 'longName');
    this.addSearchFieldsForIds(excavationLicenses, this.fieldsLists.licenses, RequestKeys.EXCAVATION_LICENSES, 'licenseId', 'longName');
    this.addSearchFieldsForIds(surveyLicenses, this.fieldsLists.licenses, RequestKeys.SURVEY_LICENSES, 'licenseId', 'longName');
    // this.addSearchFieldsForIds(expeditions, this.fieldsLists.expeditions, RequestKeys.EXPEDITIONS, 'expeditionId', 'expeditionNameHeb');
    this.addSearchFieldsForIds(entityTypes, this.fieldsLists.entityTypes, RequestKeys.ENTITY_TYPES, 'id', 'entityTypeName');
    this.addSearchFieldsTreeNodes(periodMids, this.fieldsLists.periods, RequestKeys.PERIOD_MIDS);
    this.addSearchFieldsTreeNodes(findTypeMids, this.fieldsLists.findTypes, RequestKeys.FIND_TYPE_MIDS);
    this.addSearchFieldsTreeNodes(materialMids, this.fieldsLists.materials, RequestKeys.MATERIAL_MIDS);
    // Handle advancedThesaurus - grouped by category
    this.addSearchFieldsAdvancedThesaurusByCategory(advancedThesaurus);
    expeditions?.forEach((expeditionId: string) => {
      if (!!expeditionId) {
        const expedition = this.fieldsLists.expeditions.find(exp => exp.expeditionId.toString() === expeditionId);
        if (expedition) {
          this.addSearchField(RequestKeys.EXPEDITIONS, {
            id: expeditionId, 
            label: new MultiLangStrDto(
              expedition.expeditionName.hebrew || expeditionId,
              expedition.expeditionName.english || expeditionId
            )
          });
        } else {
          this.addSearchField(RequestKeys.EXPEDITIONS, {
            id: expeditionId, 
            label: new MultiLangStrDto(expeditionId, expeditionId)
          });
        }
      }
    });
    freeText?.forEach((element: string) => {
      !!element && this.addSearchField(RequestKeys.FREE_TEXT, {id: element, label: new MultiLangStrDto(element,element)});
    });
    excavations?.forEach((excavationId: string) => {
      if (!!excavationId) {
        const excavation = this.fieldsLists.excavations.find(exc => exc.excavationId.toString() === excavationId);
        if (excavation) {
          this.addSearchField(RequestKeys.EXCAVATIONS, {
            id: excavationId, 
            label: new MultiLangStrDto(
              excavation.excavationName.hebrew || excavationId,
              excavation.excavationName.english || excavationId
            )
          });
        } else {
          this.addSearchField(RequestKeys.EXCAVATIONS, {
            id: excavationId, 
            label: new MultiLangStrDto(excavationId, excavationId)
          });
        }
      }
    });
    surveys?.forEach((surveyEntityId: string) => {
      if (!!surveyEntityId) {
        const survey = this.fieldsLists.surveys.find(srv => srv.entitySurveyId.toString() === surveyEntityId);
        if (survey) {
          this.addSearchField(RequestKeys.SURVEYS, {
            id: surveyEntityId, 
            label: survey.surveyName
          });
        } else {
          this.addSearchField(RequestKeys.SURVEYS, {
            id: surveyEntityId, 
            label: new MultiLangStrDto(surveyEntityId, surveyEntityId)
          });
        }
      }
    });
    regions?.forEach((element: string) => {
      !!element && this.addSearchField(RequestKeys.REGIONS, {id: element, label:new MultiLangStrDto(regions.length.toString() + ' פוליגונים על המפה',regions.length.toString() + ' Polygons on the map') });
    });
  }

  isDiffParamsSearchFields(params: ParamMap, previousParams: ParamMap): boolean {
    const searchFields = Object.keys(this.searchFieldsSubject.value);
    for (let field of searchFields) {
      const prevValues = previousParams.get(field)?.split(',').sort().join(',');
      const currValues = params.get(field)?.split(',').sort().join(',');
      if (prevValues !== currValues) {
        return true;
      }
    }
    return false;
  }

  clearSearchFields() {
    this.searchFieldsSubject.next({
      regionIds: [],
      conservationReports: [],
      settlementIds: [],
      persons: [],
      regions:[],
      expeditions: [],
      expeditionLicenses: [],
      excavations: [],
      excavationLicenses: [],
      surveys: [],
      surveyLicenses: [],
      hachrazot:[],
      heritageSiteIds: [],
      licenses: [],
      thesaurusWids: [],
      advancedThesaurus: [],
      periodMids: [],
      materialMids: [],
      findTypeMids: [],
      entityTypes: [],
      freeText: []
    });
  }
  
}